import qs from 'qs';

import { getProfileIdFromPrimaryIdentityId } from '@/data/helpers/profile';
import { getSlugForLocale, supportedSlugs } from '@/data/locale';
import { EVENT_TAB_PARAM, EventPageTabs } from '@/enums/eventPageTabs';
import { stringifyQS } from '@/utils';
import { Logger } from '@/utils/logger';

import getMutationUrl from './getMutationUrl';

/**
 * @param {String} url
 * @param hashObj
 * @returns {String}
 */
function addHash(url, hashObj = {}) {
  const hash = qs.stringify(hashObj);
  if (hash) {
    return `${url}#${hash}`;
  }
  return url;
}

function getQuery(query) {
  return stringifyQS(query) ? `?${stringifyQS(query)}` : '';
}

const getUrl = {
  homepage: (localeIso2, hash = undefined) => {
    const slug = getSlugForLocale(localeIso2);
    if (hash) {
      return addHash(`/${slug}`, hash);
    }
    return `/${slug}`;
  },

  /**
   * @param hash {source: String}
   * @param query {returnUrl: String}
   * @returns {String}
   */
  loginPage: ({ hash, query } = {}) => {
    const queryString = getQuery(query);
    return addHash(`/login${queryString}`, hash);
  },

  communityPage: () => {
    return '/community';
  },

  /**
   * @param hash {source: String}
   * @returns {String}
   */
  eventReviewForm: ({ eventId, permalink, hash } = {}) => {
    if (permalink) return addHash(`${permalink}/review`, hash);
    return addHash(`/e/${eventId}/review`, hash);
  },

  /**
   *
   * @param hash {source: String}
   * @returns {String}
   */
  eventReviewFormSuccess: ({ permalink, hash } = {}) => {
    return addHash(`${permalink}/review/success`, hash);
  },

  /**
   *
   * @param hashObj {edit: Boolean, source: String}
   * @returns {String}
   */
  onboarding: (hashObj) => {
    return addHash('/onboarding', hashObj);
  },

  savedEvents: () => {
    return '/saved-events';
  },

  privateProfile: () => {
    return '/my-profile';
  },

  privateProfileSettings: () => {
    return '/my-profile/settings';
  },

  privateProfileEmailSettings: () => {
    return '/my-profile/emailprefs';
  },

  publicProfile: (profileId) => {
    return `/profile/${profileId}`;
  },

  publicProfileFromPrimaryIdentityId: (primaryIdentityId) => {
    return `/profile/${getProfileIdFromPrimaryIdentityId(primaryIdentityId)}`;
  },

  publicProfileReviews: (profileId) => {
    return `/profile/${profileId}/reviews`;
  },

  refunds: () => {
    return 'https://help.letsdothis.com/article/26-how-do-i-claim-my-refund';
  },

  addEventPage: (eventId) => {
    return `/e/${eventId}/add-event`;
  },
  socialPageWithVariant: (variant) => {
    return `/labs/social/?v=${variant}`;
  },

  eventPage: (eventCountry, eventSlug, eventId) => {
    let eventKey = eventId;

    // Accomodates use of /e/eventid without slug
    if (eventSlug != null) {
      eventKey = `${eventSlug}-${eventKey}`;
    }

    let eventPath = '/e/';

    if (eventCountry && supportedSlugs.includes(eventCountry.toLowerCase())) {
      eventPath = `/${eventCountry.toLowerCase()}${eventPath}${eventKey}`;
    } else {
      eventPath = `${eventPath}${eventKey}`;
    }

    return eventPath;
  },

  eventPageWithId: (eventId) => {
    return `/e/${eventId}`;
  },

  organiserPageWithId: (organiserId) => {
    return `/o/${organiserId}`;
  },

  organiserDashboard: (organiserId) => {
    return `/dashboard/o/${organiserId}`;
  },

  organiserDashboardStartlist: (organiserId, eventId) => {
    return `/dashboard/o/${organiserId}/e/${eventId}`;
  },

  organiserSettings: (organiserId) => {
    return `/dashboard/o/${organiserId}/settings`;
  },

  organiserDashboardLatestEntries: (organiserId) => {
    return `/dashboard/o/${organiserId}/latest`;
  },

  ldtDashboardLatestEntries: () => {
    return `/dashboard/ldt/latest`;
  },

  eventConversationPageWithId: (eventId) => {
    return `/e/${eventId}?${EVENT_TAB_PARAM}=${EventPageTabs.CONVERSATION}`;
  },

  eventTicketPage: (eventCountry, eventSlug, eventId) => {
    return `${getUrl.eventPage(eventCountry, eventSlug, eventId)}/tickets`;
  },

  eventCharityEntryPage: (eventCountry, eventSlug, eventId, raceId) => {
    const racePath = raceId ? `/${raceId}` : '';
    return `${getUrl.eventPage(
      eventCountry,
      eventSlug,
      eventId,
    )}/charity-entry${racePath}`;
  },

  bookingPage: (ticketId) => {
    return `/book/${ticketId}`;
  },

  blog: () => {
    return '/blog/';
  },

  cookiePolicy: () => {
    return '/cookie-policy';
  },

  leaderboards: () => {
    return '/gb/top-rated/flagship';
  },

  // FIXME (tom, WEB-488): These methods should be
  // tier aware.
  store: () => {
    return 'https://store.letsdothis.com';
  },

  writeAReview: () => {
    return '/write-a-review';
  },

  helpCenter: () => 'https://help.letsdothis.com',
  helpCenterInjuryProtection: () =>
    'https://help.letsdothis.com/article/26-how-do-i-claim-my-refund',
  challengeAwardsBlogPost: () =>
    'https://www.letsdothis.com/blog/challenge-awards-winners',
  myEvents: () => '/my-events',
  myEvent: (bookingId, eventId) => `/my-events/${bookingId}/${eventId}`,
  allEvents: (locale = 'us') => `/${locale}/all-events`,
  virtualEventHandbook: () =>
    'https://www.letsdothis.com/blog/virtual-event-handbook',

  noBookingSubmitResultPage: (eventId) => {
    return `/e/${eventId}/submit-result`;
  },
  bookingSubmitResultPage: (eventId, bookingId) => {
    return `/e/${eventId}/submit-result/booking/${bookingId}`;
  },
  resultSubmission: (eventId, bookingId) => {
    return `/e/${eventId}/result-submissions/${bookingId}`;
  },

  // eslint-disable-next-line no-unused-vars
  results: (eventId, bookingId) => {
    return `/e/${eventId}/results`;
  },

  groupPage: (props) => {
    return props?.groupId ? `/g/${props.groupId}` : '/g/';
  },

  groupInvite: ({ groupId, eventId, encodedInvitedByUserId }) => {
    if (groupId && eventId && encodedInvitedByUserId) {
      return `/g/${groupId}/${eventId}/${encodedInvitedByUserId}`;
    }
    if (groupId && encodedInvitedByUserId) {
      return `/g/${groupId}/${encodedInvitedByUserId}`;
    }

    Logger.error(`[Route Error]: group invite is missing parameters`);
  },

  joinGroupMutation: ({ groupId, returnUrl }) => {
    return getMutationUrl('/join-group', { groupId, returnUrl });
  },

  collectBibPage: ({ eventId, eoReference, step, raceId }) => {
    if (eoReference && eventId && step && raceId) {
      return `/e/${eventId}/collect-bib/${raceId}/eoReference/${eoReference}/step/${step}`;
    }
    if (eoReference && eventId && raceId) {
      return `/e/${eventId}/collect-bib/${raceId}/eoReference/${eoReference}`;
    }
    if (eventId && raceId) {
      return `/e/${eventId}/collect-bib/eoReference/${eoReference}`;
    }
    if (eventId) {
      return `/e/${eventId}/collect-bib`;
    }
    Logger.error(`[Route Error]: bib collection is missing parameters`);
  },

  organizerClaimPage: ({ eventId, organiserId }) => {
    if (!eventId && !organiserId) {
      Logger.error(
        `[Route Error]: organizerClaim route is missing eventId or organiserId`,
      );
      return '#';
    }

    return `/organiser-claim${getQuery({ eventId, organiserId })}`;
  },
  contributionConfirmationPage: ({ eventId }) => {
    if (eventId) {
      return `/suggest-confirm/${eventId}`;
    }
    Logger.error(
      `[Route Error]: contributionConfirmationPage route is missing parameters`,
    );
  },
  dynamicBookingPage: ({ ticketId, jobId, isAdmin, useDebugger }) => {
    const query = getQuery({
      ...(useDebugger ? { debugger: true } : {}),
    });

    if (!jobId) {
      Logger.error(`[Route Error]: dynamicBookingPage route is missing jobId`);
      return null;
    }
    if (isAdmin) {
      return `/dbook/admin/job/${jobId}${query}`;
    }
    if (!ticketId) {
      Logger.error(
        `[Route Error]: dynamicBookingPage route is missing ticketId`,
      );
      return null;
    }
    return `/dbook/ticket/${ticketId}/${jobId}${query}`;
  },
  startDynamicBookingPage: ({ ticketId, isAdmin, useDebugger }) => {
    const query = getQuery({
      ...(useDebugger ? { debugger: true } : {}),
    });

    if (isAdmin) {
      return `/dbook/admin${query}`;
    }
    if (!ticketId) {
      Logger.error(
        `[Route Error]: dynamicBookingPage route is missing ticketId`,
      );
      return null;
    }
    return `/dbook/ticket/${ticketId}${query}`;
  },
};

export default getUrl;
